/**
 * Вкладка «Интеграции и импорт» — здесь живёт разбор (AK-546, ч.2; сост. 3).
 *
 * Р-3 + Р-4: статус интеграции ≠ статус справочника, а разбор — интерфейс
 * РАЗОВОЙ настройки, а не ежедневная работа. Поэтому он вынесен из справочника
 * в соседнюю вкладку: список культур работает сам по себе, даже если
 * неразобранного полно.
 *
 * Порция = источник + дата + состав (решение Б). Разные источники в одну порцию
 * не смешиваются — иначе не существует момента «эта порция разобрана».
 */
import { useState } from 'react';
import { CircleAlert, Inbox } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatRuDate } from '@/lib/dateFmt';
import { useImportQueue } from '@/lib/reference/companyOperationTypes';
import {
  useReviewBatches,
  useReviewSummary,
  type ReviewBatch,
  type ReviewEntityType,
} from '@/lib/reference/catalogReview';
import { TenantCropsPage as MatchReport } from '../TenantCropsPage';
import { BatchWizard } from './BatchWizard';
import { originDate, originLabel, plural, progressLabel } from './reviewLabels';

/** Строка «пришло 135 · уже есть 100 · разнести 35 (20 с предложением, 15 вручную)». */
function batchSummaryLine(b: ReviewBatch): string {
  const c = b.counts;
  const tail = c.needs_review > 0
    ? ` · разнести ${c.needs_review} (${c.suggested} с предложением, ${c.manual} вручную)`
    : ' · разбирать нечего';
  return `пришло ${c.total} · уже есть ${c.existing}${tail}`;
}

function BatchRow({
  batch,
  onOpen,
}: {
  batch: ReviewBatch;
  onOpen: () => void;
}) {
  const done = batch.status === 'completed';
  return (
    <Card className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={done ? 'secondary' : 'accent-brand'}>
            {originLabel(batch)}
          </Badge>
          <span className="text-sm font-semibold text-foreground">
            {formatRuDate(originDate(batch))}
          </span>
          {batch.source_label && (
            <span className="truncate text-sm text-muted-foreground">
              {batch.source_label}
            </span>
          )}
          {batch.status === 'deferred' && <Badge variant="outline">отложена</Badge>}
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{batchSummaryLine(batch)}</p>
        {!done && batch.counts.decided > 0 && (
          <p className="mt-1 text-xs text-muted-foreground">
            {progressLabel(batch.counts)}
          </p>
        )}
      </div>
      <Button
        variant={done ? 'outline' : 'accent-brand'}
        size="sm"
        className="shrink-0"
        onClick={onOpen}
      >
        {done ? 'Отчёт разбора' : 'Разобрать'}
      </Button>
    </Card>
  );
}

export function CatalogReviewTab({
  entityType,
  canRead,
  canDecide,
}: {
  entityType: ReviewEntityType;
  /** Право видеть разбор. Без него вкладку не рисуем вовсе. */
  canRead: boolean;
  /** Право принимать решения (решение Е — выдаётся адресно). */
  canDecide: boolean;
}) {
  const [openBatchId, setOpenBatchId] = useState<string | null>(null);
  const batches = useReviewBatches(entityType, canRead);
  const summary = useReviewSummary(entityType, canRead);

  if (!canRead) {
    return (
      <Alert>
        <AlertTitle>Разбор вам не выдан</AlertTitle>
        <AlertDescription>
          Это отдельное право — его выдаёт администратор холдинга, отдельно от
          права редактировать справочник.
        </AlertDescription>
      </Alert>
    );
  }

  if (openBatchId) {
    return (
      <BatchWizard
        entityType={entityType}
        batchId={openBatchId}
        canDecide={canDecide}
        onBack={() => setOpenBatchId(null)}
      />
    );
  }

  const rows = batches.data ?? [];
  const pending = rows.filter((b) => b.status !== 'completed');
  const done = rows.filter((b) => b.status === 'completed');

  return (
    <div className="flex flex-col gap-8">
      <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-base font-semibold text-foreground">Что пришло из других систем</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Каждая строка — то, что пришло из одного источника за один раз.
          Разбирается целиком: видно, когда работа кончилась.
        </p>
      </div>

      {/* «Вне порций осталось N» — честность модели порций (условие приёмки). */}
      {summary.data && summary.data.outside_batches > 0 && (
        <Alert>
          <CircleAlert className="h-4 w-4" />
          <AlertTitle>
            Отдельно ждут разбора {summary.data.outside_batches}{' '}
            {plural(summary.data.outside_batches, 'название', 'названия', 'названий')}
          </AlertTitle>
          <AlertDescription>
            Эти названия пришли позже и в списки ниже не попали. Разобрать их
            можно на вкладке «Справочник»: то, что здесь всё разобрано, ещё не
            значит, что разобрано вообще всё.
          </AlertDescription>
        </Alert>
      )}

      {batches.isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 2 }).map((_, i) => (
            <Skeleton key={i} className="h-20 w-full rounded-lg" />
          ))}
        </div>
      ) : batches.isError ? (
        <Alert variant="destructive">
          <CircleAlert className="h-4 w-4" />
          <AlertTitle>Не удалось загрузить</AlertTitle>
          <AlertDescription>
            <Button variant="outline" size="sm" onClick={() => batches.refetch()}>
              Повторить
            </Button>
          </AlertDescription>
        </Alert>
      ) : rows.length === 0 ? (
        <Card className="flex flex-col items-center gap-2 p-8 text-center">
          <Inbox className="h-6 w-6 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">Разбирать нечего</p>
          <p className="text-sm text-muted-foreground">
            Строка появится, когда данные придут из интеграции или загрузки файла.
          </p>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {pending.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-sm font-semibold text-foreground">
                Есть что разбирать
              </h4>
              {pending.map((b) => (
                <BatchRow key={b.id} batch={b} onOpen={() => setOpenBatchId(b.id)} />
              ))}
            </div>
          )}
          {done.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-sm font-semibold text-foreground">Разобранные</h4>
              <div className="overflow-hidden rounded-lg border border-border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[28%]">Источник</TableHead>
                      <TableHead className="w-[22%]">Дата</TableHead>
                      <TableHead className="w-[30%]">Состав</TableHead>
                      <TableHead className="w-[20%] text-right">&nbsp;</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {done.map((b) => (
                      <TableRow key={b.id}>
                        <TableCell>
                          <Badge variant="secondary">{originLabel(b)}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatRuDate(originDate(b))}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {b.counts.total}{' '}
                          {plural(b.counts.total, 'название', 'названия', 'названий')}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOpenBatchId(b.id)}
                          >
                            Отчёт разбора
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Таблица сопоставления живёт ЗДЕСЬ, а не в справочнике (UAT founder
          07.08). Это отчёт «что пришло и что мы с этим сделали» — он про
          интеграцию, а не про список записей, которыми работает агроном.
          У тех.операций своего такого отчёта пока нет. */}
      {entityType === 'crop' && (
        <div className="border-t border-border pt-8">
          <MatchReport />
        </div>
      )}

      {/* Заявки в системный каталог — второй канал общения с платформой, и
          живёт он ЗДЕСЬ, а не в списке (AK-564). Список — про ежедневную
          работу; заявка происходит раз в жизни названия, ответ приходит через
          дни. Кнопка отправки — в карточке названия. */}
      {entityType === 'operation_type' && (
        <div className="border-t border-border pt-8">
          <OpTypeImportRequests />
        </div>
      )}
    </div>
  );
}

const REQUEST_STATUS_LABEL: Record<string, string> = {
  pending: 'ждёт разбора',
  mapped: 'привязана к существующей',
  created: 'заведена в каталог',
  rejected: 'отклонена',
};

/** Таблица «Заявки в системный каталог» — судьба заявки целиком. */
function OpTypeImportRequests() {
  const queue = useImportQueue('all');
  // `source_system='tenant'` — заявки, отправленные изнутри хозяйства. Строки
  // ETL-очередей сюда не приплюсовываем: они не про «дополните платформу».
  const rows = (queue.data ?? []).filter((q) => q.source_system === 'tenant');

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h3 className="text-base font-semibold text-foreground">
          Заявки в системный каталог
        </h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Названия, которых нет в общем справочнике платформы. Разбирает
          platform-admin: заводит новую операцию или привязывает к существующей.
          Отправить заявку можно из карточки названия.
        </p>
      </div>
      {rows.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground">
          Заявок нет — всё, чем вы пользуетесь, уже есть в общем справочнике.
        </Card>
      ) : (
        <div className="overflow-hidden rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[48%]">Название</TableHead>
                <TableHead className="w-[30%]">Статус</TableHead>
                <TableHead className="w-[22%]">Просили раз</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((q) => (
                <TableRow key={q.id}>
                  <TableCell className="font-medium text-foreground">
                    {q.incoming_name}
                  </TableCell>
                  <TableCell>
                    <Badge variant={q.status === 'pending' ? 'accent-brand' : 'secondary'}>
                      {REQUEST_STATUS_LABEL[q.status] ?? q.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {q.occurrence_count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default CatalogReviewTab;
